// How the reasons of the last execution of a Failed test stand against the draft the user is editing on the
// duplicate page (backend plan, §6.3 "I motivi nella bozza").
//
// The reasons are a photograph of the last execution, read once from the failed test. The peers, instead,
// are recomputed from the criteria of the draft at every change. The comparison is by ISIN and is a
// calculation: nothing is deleted or changed, so if the user changes the criteria and then puts them back,
// the reasons come back by themselves.
import { failurePresentation, FAILURE_FAMILIES, FAILURE_TYPES } from './failurePresentation'

const COMPARABLE_NOT_RESOLVED = 'ComparableNotResolved'
const STATUS_EXCLUDED = 'Excluded'
const STATUS_ADDED_BY_HAND = 'Included'

const normalized = (isin) => (isin || '').trim().toUpperCase()

/**
 * @param {{ failed: boolean, failures: object[] }} lastExecution the last execution of the original test:
 *   whether it failed, and `failures[]`
 * @param {object[]} peers the items of the coverage check of the draft ({ isin, status })
 * @returns {{
 *   rows: Object<string, { presentation: object }[]>,
 *   banner: { visible: boolean, blocking: number, retry: number, notApplicable: number, notes: object[] }
 * }}
 *   `rows`: the reasons to show on the row of each ISIN, as presentations; `banner`: `blocking` certificates
 *   still to exclude, `retry` that can be tried again, `notApplicable` reasons that no longer concern the
 *   current criteria, and `notes` the presentations told in the banner only (general, or with no certificate).
 */
export function compareFailuresWithPeers(lastExecution, peers) {
  const failed = !!(lastExecution && lastExecution.failed)
  const failures = (failed && lastExecution.failures) || []
  const peerByIsin = {}
  ;(peers || []).forEach((peer) => {
    peerByIsin[normalized(peer.isin)] = peer
  })

  const rows = {}
  const notes = []
  let blocking = 0
  let retry = 0
  let notApplicable = 0

  failures.forEach((failure) => {
    // The comparable that no longer resolves is not in the draft: the criteria derive the comparables anew,
    // so it has either gone by itself or it shows as a warning of its own.
    if (failure.reason === COMPARABLE_NOT_RESOLVED) {
      notApplicable++
      return
    }

    const general = failurePresentation(failure)
    // Told in the banner, always, and nothing to fix by editing the peers.
    if (!failure.isin || general.family === FAILURE_FAMILIES.GENERAL) {
      if (!notes.some((note) => note.textKey === general.textKey)) {
        notes.push(general)
      }
      return
    }

    const peer = peerByIsin[normalized(failure.isin)]
    if (!peer) {
      notApplicable++
      return
    }
    // Excluded: resolved. The certificate is not imported nor compared any more.
    if (peer.status === STATUS_EXCLUDED) {
      return
    }

    const presentation = failurePresentation(failure, { manuallyAdded: peer.status === STATUS_ADDED_BY_HAND })
    const key = normalized(failure.isin)
    rows[key] = [...(rows[key] || []), presentation]
    if (presentation.type === FAILURE_TYPES.BLOCKING) {
      blocking++
    } else {
      retry++
    }
  })

  return { rows, banner: { visible: failed, blocking, retry, notApplicable, notes } }
}
